import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, inject } from '@angular/core';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { Delivery } from 'app/core/model/delivery';
import { DeliveryDriver } from 'app/core/model/delivery-driver';
import { DeliveryStack } from 'app/core/model/delivery-stack';
import { DeliveryTrailer } from 'app/core/model/delivery-trailer';
import { DeliveryTruck } from 'app/core/model/delivery-truck';
import { DeliveryTransportPlate } from 'app/core/model/delivery-transport-plate';
import { OrderLineDetail } from 'app/core/model/order-line-detail';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-delivery-flyout',
    templateUrl: './delivery-flyout.component.html',
    styleUrls: ['./delivery-flyout.component.scss'],
    standalone: false
})
export class DeliveryFlyoutComponent implements OnInit, OnChanges {
    private uow = inject(GenesisUnitOfWork);
    private auth = inject(AuthService);
    private appInfo = inject(AppInformationService);
    private utilityService = inject(UtilityService);
    private entitiesService = inject(EntitiesService);

    @Input() delivery: Delivery;
    @Input() readOnly: boolean = false;
    @Output() onCloseClick = new EventEmitter();

    result: Subject<any> = new Subject<any>();
    drivers: DeliveryDriver[];
    trucks: DeliveryTruck[];
    trailers: DeliveryTrailer[];
    transportPlates: DeliveryTransportPlate[];
    deliveryStacks: DeliveryStack[] = [];
    savedStacks: DeliveryStack[] = [];
    stackLimit = 9;
    showAsDialog = false;

    @ViewChild('stackDataGrid') stackDataGrid: DxDataGridComponent;

    async ngOnInit() {
        this.drivers = (await this.uow.deliveryDrivers.whereActive('displayOrder, name')) as DeliveryDriver[];
        this.trucks = (await this.uow.deliveryTrucks.whereActive('displayOrder, name')) as DeliveryTruck[];
        this.trailers = (await this.uow.deliveryTrailers.whereActive('displayOrder, name')) as DeliveryTrailer[];
        this.transportPlates = (await this.uow.deliveryTransportPlates.whereActive('displayOrder, name')) as DeliveryTransportPlate[];
    }

    // called when inputs change
    ngOnChanges() {
        if (this.delivery && this.delivery.deliveryStacks) this.deliveryStacks = this.delivery.deliveryStacks;
        else this.deliveryStacks = [];
        this.savedStacks = this.deliveryStacks;
        this.refresh();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'before',
            template: 'toolbarButtons',
        });
    }

    public async addToStack(orderLineDetailIds: number[]) {
        if (!this.delivery) this.createDelivery();

        const messages = [];
        const details = (await this.uow.orderLineDetails.byIds(orderLineDetailIds, 'orderLine.order.dealer, optionDetails.orderLine')) as OrderLineDetail[];
        const idsOnAnotherStack = await this.utilityService.getDetailIdsOnAStack(orderLineDetailIds);
        this.entitiesService.setOptionHtml(details);

        details.forEach((detail) => {
            const nextStackPosition = this.nextStackPosition();
            if (nextStackPosition > this.stackLimit) messages.push(`Part # ${detail.orderLine.partNumber}. Stack limit reached`);
            else if (this.deliveryStacks.some((stack) => stack.orderLineDetailId === detail.id)) messages.push(`Part # ${detail.orderLine.partNumber} already on stack`);
            else if (idsOnAnotherStack.includes(detail.id)) messages.push(`Part # ${detail.orderLine.partNumber} on another stack`);
            else {
                const stack = this.uow.deliveryStacks.createEntity({
                    deliveryId: this.delivery.id,
                    stackPosition: nextStackPosition,
                    orderLineDetailId: detail.id,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as DeliveryStack;
                this.deliveryStacks.push(stack);
            }
        });

        if (messages.length > 0) {
            await alert(`Could not add the following parts\n - ${messages.join('\n - ')}`, this.appInfo.settings.company);
        }
    }

    async save() {
        this.refresh();
        const stacks = _([...this.deliveryStacks, ...this.savedStacks])
            .filter((s) => !s.entityAspect.entityState.isDetached())
            .uniq()
            .valueOf();
        await this.uow.commit([...stacks, this.delivery]);
        this.savedStacks = this.deliveryStacks.slice();
        this.refresh();
    }

    cancel() {
        this.delivery.entityAspect.rejectChanges();
        this.deliveryStacks.forEach((stack) => stack.entityAspect.rejectChanges());
        this.savedStacks.forEach((stack) => stack.entityAspect.rejectChanges());
        if (this.delivery.entityAspect.entityState.isDetached()) this.close();
        this.deliveryStacks = this.savedStacks;
        this.refresh();
    }

    close() {
        this.clear();
        this.onCloseClick.emit();
    }

    clear() {
        this.delivery = null;
        this.deliveryStacks = [];
        this.savedStacks = [];
    }

    nextStackPosition() {
        return (_(this.deliveryStacks).maxBy((stack) => stack.stackPosition)?.stackPosition ?? 0) + 1;
    }

    createDelivery() {
        this.delivery = this.uow.deliveries.createEntity({
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        }) as Delivery;
    }

    onStackReorder = (e) => {
        this.refresh();

        const visibleRows = e.component.getVisibleRows();
        const toIndex = this.deliveryStacks.indexOf(visibleRows[e.toIndex].data);
        const fromIndex = this.deliveryStacks.indexOf(e.itemData);

        this.deliveryStacks.splice(fromIndex, 1);
        this.deliveryStacks.splice(toIndex, 0, e.itemData);
        let idx = this.deliveryStacks.length;
        this.deliveryStacks.forEach((stack) => {
            stack.stackPosition = idx;
            idx--;
        });

        this.refresh();
    };

    remove(stack: DeliveryStack) {
        if (stack.entityAspect.entityState.isAdded()) {
            stack.entityAspect.rejectChanges();
        } else {
            stack.deleted = new Date();
            stack.deletedBy = this.auth.currentUser.userName;
        }

        this.refresh();
    }

    refresh() {
        _(this.deliveryStacks)
            .filter((stack) => !stack.entityAspect.entityState.isDetached() && !stack.deleted)
            .orderBy((stack) => stack.stackPosition)
            .valueOf()
            .forEach((stack, i) => (stack.stackPosition = i + 1));

        this.deliveryStacks = _(this.deliveryStacks)
            .filter((stack) => !stack.entityAspect.entityState.isDetached() && !stack.deleted)
            .orderBy((stack) => stack.stackPosition, 'desc')
            .valueOf();
    }

    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }
}
